import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { MapPin, Users, Trophy, ArrowRight, Star, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import BrandLogo from '../components/BrandLogo';
import Footer from '../components/Footer';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_members: 0, total_lakes: 0, total_catches: 0 });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    fetchStats();
  }, [isAuthenticated, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  };

  const features = [
    {
      icon: MapPin,
      title: 'Lake Database',
      description: 'Access detailed information on thousands of carp fishing venues across the country.'
    },
    {
      icon: Trophy,
      title: 'Catch Records',
      description: 'Log your catches, track your personal bests, and compete on leaderboards.'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Connect with fellow anglers, share tips, and join exclusive events.'
    },
    {
      icon: Star,
      title: 'Premium Content',
      description: 'Access guides, tutorials, and exclusive deals from top tackle brands.'
    }
  ];

  const tiers = [
    {
      name: 'Basic',
      price: 'Free',
      period: 'forever',
      features: ['Public community feed', 'Basic catch uploads', 'Public leaderboards', 'Community discussions'],
      highlight: false,
    },
    {
      name: 'Pro',
      price: '£4.99',
      period: '/ month',
      features: ['Detailed lake intel', 'Smart bait recommendations', 'Catch analytics & trends', 'Monthly competitions', '3-day free trial'],
      highlight: true,
      badge: 'Most Popular',
    },
    {
      name: 'Elite',
      price: '£9.99',
      period: '/ month',
      features: ['Everything in Pro', 'Elite-only competitions', 'Higher-value giveaways', 'Fishery discounts', 'Priority support'],
      highlight: false,
    }
  ];

  return (
    <div className="min-h-screen bg-[#050605]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1738597602799-62422e1eeb8a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxtaXN0eSUyMGxha2UlMjBmaXNoaW5nJTIwZGF3biUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzExNjg0MDZ8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
            <Link to="/" className="flex items-center group" data-testid="landing-logo-link">
              <BrandLogo
                variant="icon"
                size="md"
                as="div"
                className="sm:hidden"
                testId="landing-logo-icon"
              />
              <BrandLogo
                variant="full"
                size="lg"
                as="div"
                className="hidden sm:inline-flex"
                testId="landing-logo-full"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="btn-ghost" data-testid="login-btn">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-gold" data-testid="register-btn">
                  Join Now
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <BrandLogo
              variant="full"
              size="splash"
              as="div"
              className="mx-auto mb-8"
              glow
              testId="hero-logo"
            />
            <p className="data-label text-[#c8b273] mb-4">The Premier Carp Angling Community</p>
            <h1 className="font-['Barlow_Condensed'] text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-wider text-white mb-6">
              Where Legends<br />Are Made
            </h1>
            <p className="text-lg text-[#8a948a] max-w-2xl mx-auto mb-8">
              Join thousands of passionate carp anglers. Access exclusive lake data, track your catches, 
              and connect with a community that lives for the thrill of the catch.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button className="btn-gold text-lg px-8 py-4" data-testid="hero-join-btn">
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#features">
                <Button variant="ghost" className="btn-ghost text-lg px-8 py-4">
                  Learn More
                  <ChevronDown className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="mt-16 grid grid-cols-3 gap-8 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="text-center">
              <p className="font-['Barlow_Condensed'] text-3xl font-bold text-[#c8b273]">
                {stats.total_members || '500+'}
              </p>
              <p className="data-label text-[#8a948a] mt-1">Members</p>
            </div>
            <div className="text-center">
              <p className="font-['Barlow_Condensed'] text-3xl font-bold text-[#c8b273]">
                {stats.total_lakes || '200+'}
              </p>
              <p className="data-label text-[#8a948a] mt-1">Lakes</p>
            </div>
            <div className="text-center">
              <p className="font-['Barlow_Condensed'] text-3xl font-bold text-[#c8b273]">
                {stats.total_catches || '10K+'}
              </p>
              <p className="data-label text-[#8a948a] mt-1">Catches</p>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-[#c8b273]" />
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="data-label text-[#c8b273] mb-4">Why Choose The Carp Cast</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white">
              Everything You Need
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="glass-card p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <feature.icon className="w-10 h-10 text-[#c8b273] mb-4" />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#8a948a] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 md:px-12 bg-[#0b0d0b]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="data-label text-[#c8b273] mb-4">Membership Tiers</p>
            <h2 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white">
              Choose Your Level
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                className={`relative glass-card p-8 ${tier.highlight ? 'elite-badge' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c8b273] text-black text-xs font-bold uppercase tracking-widest px-4 py-1">
                    Most Popular
                  </div>
                )}
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white mb-2">
                  {tier.name}
                </h3>
                <div className="mb-6">
                  <span className="font-['Barlow_Condensed'] text-4xl font-extrabold text-[#c8b273]">
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span className="text-[#8a948a] text-sm ml-2">{tier.period}</span>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-[#8a948a]">
                      <Star className="w-4 h-4 text-[#c8b273]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className={tier.highlight ? 'btn-gold w-full' : 'btn-ghost w-full'}>
                    Get Started
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-6">
            Ready to Join the Community?
          </h2>
          <p className="text-lg text-[#8a948a] mb-8">
            Start your journey today and become part of the UK's premier carp angling community.
          </p>
          <Link to="/register">
            <Button className="btn-gold text-lg px-10 py-4" data-testid="cta-join-btn">
              Join The Carp Cast Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
